"use client";

import React, { useState } from 'react';
import ProfileSettings from './ProfileSettings';
import SecurityMFASettings from './SecurityMFASettings';
import PreferencesSettings from './PreferencesSettings';
import APISettings from './APISettings';
import ComplianceChecklist from './ComplianceChecklist';
import NotificationSettings from './NotificationSettings';
import { User, Shield, Sliders, Code2, BookOpen, Bell } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import HeroBanner from '@/components/HeroBanner';

type SettingsTab = 'profile' | 'security' | 'preferences' | 'api' | 'compliance' | 'notifications';

export default function SettingsContent() {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const tabs = [
    { key: 'profile' as SettingsTab, icon: <User size={15} />, label: 'Profile' },
    { key: 'security' as SettingsTab, icon: <Shield size={15} />, label: 'Security & MFA' },
    { key: 'preferences' as SettingsTab, icon: <Sliders size={15} />, label: 'Preferences' },
    { key: 'notifications' as SettingsTab, icon: <Bell size={15} />, label: 'Notifications' },
    { key: 'api' as SettingsTab, icon: <Code2 size={15} />, label: 'API Keys' },
    { key: 'compliance' as SettingsTab, icon: <BookOpen size={15} />, label: 'BD Compliance' },
  ];

  return (
    <div className="space-y-6 fade-in-up">
      <HeroBanner variant="compact" />
      {/* Header */}
      <div>
        <h1 className={`text-2xl font-bold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
          {t(language, 'settings_title')}
        </h1>
        <p className="text-sm text-cyber-muted mt-0.5 font-mono">
          Manage your account, security, and platform configuration
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Nav */}
        <div className="lg:w-52 shrink-0">
          <nav className="space-y-1" aria-label="Settings navigation">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                aria-current={activeTab === tab.key ? 'page' : undefined}
                aria-label={`${tab.label} settings`}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-all duration-150 text-left
                  ${activeTab === tab.key
                    ? 'bg-cyber-blue/10 text-cyber-blue border border-cyber-blue/20' :'text-cyber-muted hover:bg-white/5 hover:text-cyber-text border border-transparent'
                  }
                  ${language === 'bn' ? 'font-bangla' : ''}
                `}
              >
                <span className={activeTab === tab.key ? 'text-cyber-blue' : 'text-cyber-muted'}>
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Platform Info */}
          <div className="mt-6 px-3 py-3 bg-[#0F1117] border border-cyber-border rounded-lg">
            <p className="text-[10px] text-cyber-muted font-mono uppercase tracking-widest mb-2">Platform</p>
            <div className="space-y-1 text-[11px] font-mono">
              <div className="flex justify-between">
                <span className="text-cyber-muted">Version</span>
                <span className="text-cyber-text">v3.2.1</span>
              </div>
              <div className="flex justify-between">
                <span className="text-cyber-muted">License</span>
                <span className="text-cyber-green">Enterprise</span>
              </div>
              <div className="flex justify-between">
                <span className="text-cyber-muted">Org</span>
                <span className="text-cyber-text">BTRC</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && <ProfileSettings />}
          {activeTab === 'security' && <SecurityMFASettings />}
          {activeTab === 'preferences' && <PreferencesSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'api' && <APISettings />}
          {activeTab === 'compliance' && <ComplianceChecklist />}
        </div>
      </div>
    </div>
  );
}